import React, {useEffect, useRef} from 'react';
import {FaFileDownload} from 'react-icons/fa';

interface DownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
  status: 'preparing' | 'processing' | 'downloading' | 'complete';
  title?: string;
  customMessages?: {
    preparing?: string;
    processing?: string;
    downloading?: string;
    complete?: string;
  };
  waitMessage?: string;
  buttonText?: string;
  icon?: React.ReactNode;
}

const DownloadModal: React.FC<DownloadModalProps> = ({
  isOpen,
  onClose,
  status,
  title = 'Document Download',
  customMessages = {},
  waitMessage = 'Please keep this page open until the download is complete',
  buttonText = 'Close',
  icon = <FaFileDownload className="size-8" />,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  if (!isOpen) return null;

  const getStatusMessage = () => {
    const defaultMessages = {
      preparing: 'Preparing documents for processing...',
      processing: 'Processing and combining documents...',
      downloading: 'Starting download...',
      complete: 'Download complete!',
    };

    switch (status) {
      case 'preparing':
        return customMessages.preparing || defaultMessages.preparing;
      case 'processing':
        return customMessages.processing || defaultMessages.processing;
      case 'downloading':
        return customMessages.downloading || defaultMessages.downloading;
      case 'complete':
        return customMessages.complete || defaultMessages.complete;
      default:
        return 'Please wait...';
    }
  };

  const getProgressWidth = () => {
    switch (status) {
      case 'preparing':
        return '25%';
      case 'processing':
        return '75%';
      case 'downloading':
        return '90%';
      case 'complete':
        return '100%';
      default:
        return '0%';
    }
  };

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto bg-gray-900 bg-opacity-50 flex items-center justify-center">
      <div ref={modalRef} className="bg-white rounded-lg w-full max-w-md mx-4">
        <div className="p-6">
          <div className="flex flex-col items-center">
            <div className="h-16 w-16 rounded-full bg-cyan-100 text-cyan-500 flex items-center justify-center mb-4">
              {icon}
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
            <p className="text-sm text-gray-500 text-center mb-4">
              {getStatusMessage()}
            </p>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
              <div
                className="bg-cyan-500 h-2.5 rounded-full transition-all duration-300"
                style={{width: getProgressWidth()}}
              />
            </div>
            <p className="text-xs text-amber-600 mb-4">{waitMessage}</p>
            {status === 'complete' && (
              <button
                onClick={onClose}
                className="mt-2 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-cyan-500 hover:bg-cyan-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500">
                {buttonText}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadModal;
